import { formatDecimal } from './percent'

/**
 * The tax a line was sold under, as the order detail states it — FRONTEND.md §9.5, the DPH bullet.
 *
 * **Why the line and not the product.** §10A.3 snapshots `vat_rate` and `vat_letter` onto
 * `order_items` at the moment of sale, precisely because a product's rate moves afterwards. Measured
 * on the restored data: the stored line rate and the product's current one disagree on **16 798 of
 * 21 768 lines, 77.2 %** — so reading the product would restate three orders in four. This shows
 * what was charged, which is the only thing an order detail can honestly claim.
 *
 * **The letter is not derived here.** `VatLetters.forRate` bands a rate into a letter and the
 * frontend keeps a second copy of that banding in `manager-kasa`, guarded from both sides since
 * FB-192 — a line charged at 19 % went out under **A** instead of **B** and booked the sale into the
 * wrong slot. The line already carries the letter it was registered under, so deriving one here
 * would be a third copy and could disagree with the receipt for the same sale.
 *
 * A line written before the snapshot existed carries neither, and gets nothing rather than a guess.
 */
export interface LineVat {
  /**
   * A number on `GET /orders`, the string `"19.00"` on `GET /fiskal/receipts`.
   *
   * Not a defect on either side and not worth reconciling: the receipts query reads the column
   * through `LegacyRows.of`, which keeps a numeric as the text Postgres sent, and the orders
   * projection reads it typed. Both are the same rate. What matters is that a reader of one endpoint
   * cannot write a helper against the other's type and have it quietly return half an answer, which
   * is what this accepted before 2026-08-05 — a receipt line kept its letter and silently lost its
   * rate.
   */
  vatRate?: number | string | null
  vatLetter?: string | null
}

/**
 * `A · 5 %`, or an empty string for a line that predates the snapshot.
 *
 * Letter first, matching the register's own ordering and `choiceLabel` in the product editor, so the
 * same sale reads the same way in the editor, on the detail and on the receipt.
 */
export function lineVatLabel(line: LineVat): string {
  const letter = typeof line.vatLetter === 'string' ? line.vatLetter.trim() : ''
  // A blank string is *no rate*, not zero. `Number('')` is 0, and 0 is a real rate — `D`, exempt —
  // so converting without this turns an empty column into a claim that the line was tax-exempt.
  const text = typeof line.vatRate === 'string' ? line.vatRate.trim() : null
  const raw = text === null ? line.vatRate : text === '' ? null : Number(text)
  const rate = typeof raw === 'number' && Number.isFinite(raw) ? raw : null

  if (letter === '' && rate === null) return ''
  if (letter === '') return `${formatDecimal(rate ?? 0, 2)} %`
  if (rate === null) return letter
  return `${letter} · ${formatDecimal(rate, 2)} %`
}

/**
 * Whether an order's lines were all taxed the same way.
 *
 * A single-rate order is still almost all of them, and repeating `A · 5,00 %` down every line of one
 * is noise. Where the rates differ — food at 5 % beside a drink at 19 % — the per-line label is the
 * whole point, because the total alone cannot say which part was taxed at what.
 */
export function ordersLinesShareOneRate(lines: readonly LineVat[]): boolean {
  const labels = new Set(lines.map(lineVatLabel).filter((label) => label !== ''))
  return labels.size <= 1
}

/**
 * The one label an order carries when every line shares it, for stating it once instead of never.
 *
 * **Measured before this existed, and it is why it exists.** Of 33 169 stored lines every one has a
 * letter, two are in use — A on 10 187, B on 22 982 — and **no order mixes them**: A is dine-in at
 * 5 %, B is delivery and drinks at 19 %, so the rate follows the channel and a single bill is
 * uniform. A detail that drew the tax only on a mixed-rate order would therefore have drawn it on
 * none of the 10 785 orders in the restored data, which is not what §9.5 asks for.
 *
 * So: per line where they differ, once for the order where they do not, and nothing only when the
 * lines carry no letter at all.
 */
export function sharedVatLabel(lines: readonly LineVat[]): string {
  const labels = new Set(lines.map(lineVatLabel).filter((label) => label !== ''))
  return labels.size === 1 ? [...labels][0]! : ''
}
